import type {
  CartItem,
  InitOptions,
  NegocioInteligenteClient,
  PublicWebsiteConfiguration,
  TrackProperties,
} from './types'

const DEFAULT_API_BASE_URL = 'https://api.negociointeligente.cl'

export class WebClient implements NegocioInteligenteClient {
  private configuration: PublicWebsiteConfiguration | null = null
  private personId: string | null = null
  private items = new Map<string, CartItem>()

  async init(options: InitOptions): Promise<PublicWebsiteConfiguration> {
    if (!/^NIWS-[A-Z0-9]+$/i.test(options.websiteId)) {
      throw new Error('Website ID inválido')
    }

    const apiBaseUrl = (options.apiBaseUrl ?? DEFAULT_API_BASE_URL).replace(/\/$/, '')
    const endpoint = `${apiBaseUrl}/api/v1/public/websites/${encodeURIComponent(options.websiteId)}/config`
    const response = await fetch(endpoint, {
      headers: { Accept: 'application/json' },
      credentials: 'omit',
      referrerPolicy: 'strict-origin-when-cross-origin',
    })

    if (!response.ok) {
      throw new Error(`No fue posible iniciar Negocio Inteligente (${response.status})`)
    }

    this.configuration = await response.json() as PublicWebsiteConfiguration
    this.dispatch('ready', this.configuration)
    return this.configuration
  }

  getConfiguration(): PublicWebsiteConfiguration | null {
    return this.configuration
  }

  identify(personId: string, traits: TrackProperties = {}): void {
    this.personId = personId
    this.dispatch('identify', { personId, traits })
  }

  track(event: string, properties: TrackProperties = {}): void {
    this.dispatch('track', { event, properties, personId: this.personId })
  }

  readonly cart = {
    add: (item: CartItem): void => {
      this.items.set(item.id, { ...item })
      this.dispatch('cart:changed', this.cart.getItems())
    },
    remove: (itemId: string): void => {
      this.items.delete(itemId)
      this.dispatch('cart:changed', this.cart.getItems())
    },
    clear: (): void => {
      this.items.clear()
      this.dispatch('cart:changed', [])
    },
    getItems: (): CartItem[] => Array.from(this.items.values()).map((item) => ({ ...item })),
  }

  private dispatch(name: string, detail: unknown): void {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(`ni:${name}`, { detail }))
    }
  }
}
